import React from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';
import RegionCascaderSelect from '@home/components/RegionCascaderSelect';
import RegionSelector from '@intelligence/components/RegionSelector';
import useLocale from '@home/components/NewRegionSelect/useLocale';
import { getText } from '@home/components/NewRegionSelect/util';
import { deriveRegionValue } from '@intelligence/v3/utils/region';
import { getPreviewTitle, getRegionSubOptions } from '../previewHelpers';
import { getStringArrayValue } from '../shared/valueUtils';
import { LOCALE_CN, VALUE_TYPE_REGION } from '@home/components/NewRegionSelect/config';
import { FILTER_TYPE } from '../filterTypes';

import type { IFilterRenderProps, IFilterTypeEntry } from '@intelligence/v3/filterCore/registry';
import type { IFilterControlModule } from '../controlModule';
import type { IRegionFilterValue } from './types';

const MARKET_RADIO_OPTIONS = [
  { value: 'global', text: 'Global', textCN: '全球' },
  {
    value: 'global_wo_china',
    text: 'Global except China mainland',
    textCN: '全球（不包括中国大陆）',
  },
];

const RegionCascaderSelectRenderer: React.FC<IFilterRenderProps<IRegionFilterValue>> = (props) => {
  const {
    field,
    value,
    onChange,
    options = [],
    locale,
    title,
    placeholder,
    label,
    className,
    overlayClassName,
    multiCountryText,
    onClickMultiCountry,
    ...rest
  } = props;
  const { formatMessage } = useIntl();
  const selectedCountry = getStringArrayValue(value)[0] || '';
  const labelText = title ? formatMessage({ id: title }) : label;
  const placeholderText = placeholder ? formatMessage({ id: placeholder }) : undefined;

  return (
    <RegionCascaderSelect
      {...rest}
      key={field}
      locale={locale}
      className={classnames('search-item', className)}
      overlayClassName={overlayClassName}
      label={labelText}
      placeholder={placeholderText}
      regions={options}
      value={selectedCountry}
      multiCountryText={multiCountryText}
      onClickMultiCountry={onClickMultiCountry}
      onChange={(nextValue: string) => {
        const normalized = `${nextValue || ''}`.trim().toLowerCase();
        onChange(normalized ? [normalized] : []);
      }}
    />
  );
};

const RegionSelectRenderer: React.FC<IFilterRenderProps<IRegionFilterValue>> = (props) => {
  const { singleSelect, ...restProps } = props;

  if (singleSelect) {
    return <RegionCascaderSelectRenderer {...restProps} singleSelect={singleSelect} />;
  }

  const { field, value, onChange, options, ...rest } = restProps;
  const { locale } = useIntl();
  const regionValue = getStringArrayValue(value);
  const regionSelectorValue = deriveRegionValue(regionValue.length ? regionValue : ['global'], options ?? []);

  return (
    <RegionSelector
      {...rest}
      key={field}
      locale={locale}
      options={options}
      value={regionSelectorValue}
      onConfirm={(val: any) => {
        const markets = val?.markets ?? [];
        onChange(markets.map((code: string) => code?.toLowerCase()));
      }}
    />
  );
};

const RegionPreview: React.FC<{
  item: Parameters<NonNullable<IFilterTypeEntry['renderPreview']>>[0];
  valueItem: string[];
  options: any[];
  singleSelect?: boolean;
}> = (props) => {
  const { item, valueItem, options, singleSelect } = props;
  const { locale, formatMessage: formatMessageUMI } = useIntl();
  const { formatMessage } = useLocale(locale, {});
  const allChildren = getRegionSubOptions(options);
  const customTitle = getPreviewTitle(item, formatMessageUMI);
  const titleRegion = formatMessage({ id: singleSelect ? 'dbi_region' : 'button_region' });
  const titleMarket = formatMessage({ id: 'button_market' });
  const regionValue = getStringArrayValue(valueItem);
  const previewValue = singleSelect ? regionValue.slice(0, 1) : regionValue;
  const { type, regions, markets } = deriveRegionValue(previewValue ?? [], options);

  if (!singleSelect && type === VALUE_TYPE_REGION) {
    if (regions.length === 1) {
      const findRegion = options?.find((item: any) => item.area_code === regions[0]);
      const formatRegion = {
        text: findRegion?.area_name_en,
        textCN: findRegion?.area_name_ch,
        value: findRegion?.area_code,
      };

      return (
        <>
          <span className="model-form-text">{customTitle || titleRegion}</span>
          <span className="model-form-context">{getText(formatRegion, locale) || regions[0]}</span>
        </>
      );
    }

    return (
      <>
        <span className="model-form-text">{customTitle || titleRegion}</span>
        <span className="model-form-context">{regions.length || ''}</span>
      </>
    );
  }

  if (markets.length === 1) {
    const oneMarket = markets[0];
    const findRadio = MARKET_RADIO_OPTIONS.find((item) => item.value === oneMarket);

    if (!singleSelect && findRadio) {
      return (
        <>
          <span className="model-form-text">{customTitle || titleRegion}</span>
          <span className="model-form-context">{locale === LOCALE_CN ? findRadio.textCN : findRadio.text}</span>
        </>
      );
    }

    const findMarket = allChildren?.find((item: any) => item?.value === oneMarket);

    return (
      <>
        <span className="model-form-text">{singleSelect ? titleRegion : (customTitle || titleMarket)}</span>
        <span className="model-form-context">{getText(findMarket, locale) || oneMarket}</span>
      </>
    );
  }

  return (
    <>
      <span className="model-form-text">{singleSelect ? titleRegion : (customTitle || titleMarket)}</span>
      <span className="model-form-context">{markets.length || ''}</span>
    </>
  );
};

const getRenderPreview = (): NonNullable<IFilterTypeEntry['renderPreview']> => {
  // eslint-disable-next-line react/display-name
  return (item, value) => {
    const options = item.controlProps?.options || [];
    const singleSelect = Boolean(item.controlProps?.singleSelect);

    return <RegionPreview item={item} valueItem={value} options={options} singleSelect={singleSelect} />;
  };
};

export const regionSelectControl: IFilterControlModule = {
  type: FILTER_TYPE.REGION_SELECT,
  entry: {
    render: (props) => {
      return <RegionSelectRenderer {...props} />;
    },
  },
  getRenderPreview,
};
